import { useState, useMemo } from "react";
import "@/App.css";
import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { projects, categories } from "@/data/projects";
import { ProjectCard } from "@/components/ProjectCard";
import { PreviewModal } from "@/components/PreviewModal";

const HERO_BG =
  "https://images.pexels.com/photos/33175916/pexels-photo-33175916.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

function App() {
  const [active, setActive] = useState("All");
  const [selected, setSelected] = useState(null);

  const filtered = useMemo(
    () => (active === "All" ? projects : projects.filter((p) => p.category === active)),
    [active]
  );

  return (
    <div className="min-h-screen bg-[#030303] text-[#EDEDED] font-[Manrope] antialiased selection:bg-white selection:text-black overflow-x-hidden">
      {/* ===== Hero ===== */}
      <header className="relative min-h-[88vh] flex flex-col justify-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#030303]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(255,255,255,0.08),transparent_55%)]" />
        </div>

        {/* nav */}
        <nav className="absolute top-0 inset-x-0 z-10 px-6 md:px-12 lg:px-24 py-7 flex items-center justify-between">
          <img
            src="/innersource-ai-logo.png"
            alt="Innersource AI"
            data-testid="brand-logo"
            className="h-8 md:h-9 w-auto object-contain"
          />
          <a
            href="https://innersource.ai"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-contact-link"
            className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/55 hover:text-white transition-colors"
          >
            innersource.ai
          </a>
        </nav>

        <div className="relative z-10 px-6 md:px-12 lg:px-24 pb-16 md:pb-24">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-mono text-[11px] md:text-xs uppercase tracking-[0.35em] text-white/50 mb-6"
          >
            Web Design & Development Studio
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="font-[Outfit] font-light tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-8xl max-w-5xl"
          >
            Welcome to Innersource.
            <br />
            <span className="font-extrabold">See what we build.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-base md:text-lg text-white/55 leading-relaxed"
          >
            A sample of the websites we design and build for our clients —
            from industrial enterprises to luxury real estate. Click any project
            to preview it live, then picture what we could create for your business.
          </motion.p>
          <motion.button
            type="button"
            onClick={() =>
              document.getElementById("portfolio")?.scrollIntoView({ behavior: "smooth" })
            }
            data-testid="explore-portfolio-link"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="group mt-10 flex items-center gap-2 text-white/40 hover:text-white font-mono text-[11px] uppercase tracking-[0.25em] transition-colors cursor-pointer"
          >
            <ArrowDownRight size={16} className="transition-transform group-hover:translate-y-0.5" />
            Explore the portfolio
          </motion.button>
        </div>
      </header>

      {/* ===== Portfolio ===== */}
      <main id="portfolio" className="scroll-mt-8 px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12 md:mb-16">
          <h2 className="font-[Outfit] text-3xl md:text-4xl font-semibold tracking-tight max-w-md">
            Projects we're proud of.
          </h2>
          <div className="flex flex-wrap gap-2.5" data-testid="filter-bar">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-5 py-2 rounded-full border text-sm font-medium transition-all duration-300 ${
                  active === cat
                    ? "bg-white text-black border-white"
                    : "border-white/12 text-white/60 hover:text-white hover:border-white/30"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8"
          data-testid="projects-grid"
        >
          {filtered.map((project, i) => (
            <ProjectCard key={project.id} project={project} index={i} onOpen={setSelected} />
          ))}
        </motion.div>
      </main>

      {/* ===== Footer ===== */}
      <footer className="border-t border-white/[0.07] px-6 md:px-12 lg:px-24 py-14">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <p className="font-[Outfit] text-2xl md:text-3xl font-semibold tracking-tight max-w-md leading-tight">
              Have a project in mind?
            </p>
            <p className="mt-3 text-white/50 text-sm">
              Let's build something your customers will remember.
            </p>
          </div>
          <a
            href="https://innersource.ai"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-cta"
            className="inline-flex items-center gap-2 rounded-full bg-white text-black text-sm font-semibold px-7 py-3.5 hover:bg-white/85 transition-colors"
          >
            Work with innersource.ai
            <ArrowDownRight size={16} className="-rotate-90" />
          </a>
        </div>
        <p className="mt-12 font-mono text-[10px] uppercase tracking-[0.25em] text-white/30">
          © {new Date().getFullYear()} innersource.ai — Web Design & Development
        </p>
      </footer>

      <PreviewModal project={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

export default App;
